import React, { useEffect, useMemo, useState } from 'react';
import { adminApi } from '../api';
import { fmtDate, fmtTime } from '../format';
import s from '../workspace.module.css';
import { zonedWallTimeToUtcIso } from './useSortedTimezones';
import { US_TIMEZONES, utcToZonedWallTime } from '../usTimezones';

// The host's day as a time grid, so a booking lands where it fits (what Practice Better shows while scheduling). The
// host's real Google events + our bookings for the chosen date (the Team Calendar read), with the proposed slot drawn in
// place and anything it overlaps flagged. Only a window of hours around the chosen time is drawn, so the events next
// to it are big enough to read; anything else that day is listed underneath. Times are shown in `tz` — the same zone
// the date and time fields use.

export const HOUR_PX = 64;
export const WINDOW_HOURS = 6;
const nextYmd = (ymd) => { const [y, m, d] = ymd.split('-').map(Number); return new Date(Date.UTC(y, m - 1, d + 1)).toISOString().slice(0, 10); };
const overlaps = (a, b) => a.startMin < b.endMin && b.startMin < a.endMin;
const tzLabel = (v) => US_TIMEZONES.find((o) => o.value === v)?.label || v;
const hourLabel = (h) => (h === 0 ? '12 AM' : h < 12 ? `${h} AM` : h === 12 ? '12 PM' : `${h - 12} PM`);

// Minutes from the day's midnight in `tz`, clamped to the day: an event spilling in from yesterday starts at 0, one
// running past midnight ends at 1440.
function minutesInto(iso, date, tz) {
  const at = utcToZonedWallTime(iso, tz);
  if (at.date < date) return 0;
  if (at.date > date) return 24 * 60;
  const [h, m] = at.time.split(':').map(Number);
  return h * 60 + m;
}

// The hours drawn: two before the chosen time through four after (the working morning when no time is chosen yet).
export function windowStart(slotMin) {
  const anchor = slotMin == null ? 8 * 60 : slotMin;
  return Math.min(Math.max(Math.floor(anchor / 60) - 2, 0), 24 - WINDOW_HOURS);
}

// Side-by-side columns for overlapping events (first column that is free, like a calendar does).
function packColumns(items) {
  const sorted = [...items].sort((a, b) => a.startMin - b.startMin || b.endMin - a.endMin);
  const colEnds = [];
  for (const it of sorted) {
    let col = colEnds.findIndex((end) => end <= it.startMin);
    if (col === -1) { col = colEnds.length; colEnds.push(0); }
    colEnds[col] = it.endMin;
    it.col = col;
  }
  const cols = colEnds.length || 1;
  for (const it of sorted) it.cols = cols;
  return sorted;
}

// `excludeBookingId`: the booking being edited — its current time is left out, the proposed block stands in for it.
export default function HostDaySchedule({ hostId, hostName, date, tz, slotIso, durationMinutes = 30, excludeBookingId }) {
  const [state, setState] = useState({ events: [], error: null, loading: false });

  useEffect(() => {
    if (!hostId || !date) return undefined;
    let active = true;
    setState((st) => ({ ...st, loading: true }));
    const start = zonedWallTimeToUtcIso(date, '00:00', tz);
    const end = zonedWallTimeToUtcIso(nextYmd(date), '00:00', tz);
    adminApi.get('/admin/calendar/events', { start, end, hosts: hostId })
      .then((r) => {
        if (!active) return;
        const host = (r.data.hosts || []).find((h) => h.host_id === hostId);
        setState({ events: r.data.events || [], error: host?.error || null, loading: false });
      })
      .catch(() => { if (active) setState({ events: [], error: 'Could not load the calendar.', loading: false }); });
    return () => { active = false; };
  }, [hostId, date, tz]);

  const { blocks, elsewhere, proposed, allDay, fromHour } = useMemo(() => {
    if (!date) return { blocks: [], elsewhere: [], proposed: null, allDay: [], fromHour: 8 };
    const own = state.events.filter((e) => !excludeBookingId || e.booking?.booking_id !== excludeBookingId);
    const items = own.filter((e) => !e.all_day).map((e) => ({
      key: e.id, start: e.start_utc, end: e.end_utc,
      startMin: minutesInto(e.start_utc, date, tz), endMin: minutesInto(e.end_utc, date, tz),
      title: e.busy_only ? 'Busy' : (e.booking?.patient_name || e.title || 'Busy'),
      sub: e.booking?.session_title || null,
    })).filter((it) => it.endMin > it.startMin);
    let prop = null;
    if (slotIso) {
      const endIso = new Date(new Date(slotIso).getTime() + durationMinutes * 60000).toISOString();
      prop = { start: slotIso, end: endIso, startMin: minutesInto(slotIso, date, tz), endMin: minutesInto(endIso, date, tz) };
      for (const it of items) it.conflict = overlaps(it, prop);
      prop.conflict = items.some((it) => it.conflict);
    }
    const from = windowStart(prop ? prop.startMin : null);
    const win = { startMin: from * 60, endMin: (from + WINDOW_HOURS) * 60 };
    const inWindow = items.filter((it) => overlaps(it, win));
    const outside = items.filter((it) => !overlaps(it, win)).sort((a, b) => a.startMin - b.startMin);
    return { blocks: packColumns(inWindow), elsewhere: outside, proposed: prop, fromHour: from,
      allDay: own.filter((e) => e.all_day).map((e) => (e.busy_only ? 'Busy' : e.title)) };
  }, [state.events, slotIso, durationMinutes, excludeBookingId, date, tz]);

  const ready = hostId && date;
  const winStart = fromHour * 60;
  const winEnd = (fromHour + WINDOW_HOURS) * 60;
  const box = (it) => {
    const a = Math.max(it.startMin, winStart);
    const b = Math.min(it.endMin, winEnd);
    return {
      top: ((a - winStart) / 60) * HOUR_PX + 1,
      height: Math.max(16, ((b - a) / 60) * HOUR_PX - 2),
      left: `calc(${(it.col / it.cols) * 100}% + 2px)`,
      width: `calc(${(1 / it.cols) * 100}% - 4px)`,
    };
  };
  return (
    <section className={s.daySchedule} aria-label={`${hostName || 'Host'}’s schedule`} aria-busy={state.loading || undefined}>
      <div className={s.dayScheduleHead}>
        <h3>{hostName || 'Host'}’s day</h3>
        {ready && <span>{fmtDate(zonedWallTimeToUtcIso(date, '12:00', tz), { tz })} · {tzLabel(tz)}</span>}
      </div>
      {!ready ? (
        <p className={s.detailNote}>Choose a host and a date to see their schedule.</p>
      ) : (
        <>
          {allDay.length > 0 && <p className={s.dayAllDay}>All day: {allDay.join(', ')}</p>}
          <div className={s.dayGrid} data-loading={state.loading || undefined}>
            <div className={s.dayHours} aria-hidden="true">
              {Array.from({ length: WINDOW_HOURS }, (_, i) => <span key={i} style={{ height: HOUR_PX }}>{hourLabel(fromHour + i)}</span>)}
            </div>
            <div className={s.dayColumn} style={{ height: WINDOW_HOURS * HOUR_PX }}>
              {Array.from({ length: WINDOW_HOURS }, (_, i) => <div key={i} className={s.dayHourRule} style={{ height: HOUR_PX }} />)}
              {blocks.map((it) => (
                <div key={it.key} className={s.dayEvent} data-event data-conflict={it.conflict || undefined} style={box(it)}
                  title={`${it.title} · ${fmtTime(it.start, { tz })} – ${fmtTime(it.end, { tz })}`}>
                  <strong>{it.title}</strong>
                  {it.endMin - it.startMin >= 30 && <span>{fmtTime(it.start, { tz })} – {fmtTime(it.end, { tz })}{it.sub && ` · ${it.sub}`}</span>}
                </div>
              ))}
              {proposed && (
                <div className={s.dayProposed} data-event data-proposed data-conflict={proposed.conflict || undefined}
                  style={box({ ...proposed, col: 0, cols: 1 })}>
                  <strong>This booking</strong>
                  <span>{fmtTime(proposed.start, { tz })} – {fmtTime(proposed.end, { tz })}{proposed.conflict && ' · Overlaps an existing event'}</span>
                </div>
              )}
            </div>
          </div>
          {elsewhere.length > 0 && (
            <p className={s.dayAllDay}>Also that day: {elsewhere.map((it) => `${fmtTime(it.start, { tz })} ${it.title}`).join(' · ')}</p>
          )}
          {state.error && <p className={s.dayAllDay}>Google Calendar unavailable for this host ({state.error}); only portal bookings are shown.</p>}
        </>
      )}
    </section>
  );
}
